import React, { useMemo } from "react";

import { faClock, faLink } from "@fortawesome/pro-solid-svg-icons";
import { faRedoAlt } from "@fortawesome/pro-solid-svg-icons/faRedoAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";

import { EntityType } from "@mapmycustomers/shared";
import AnyColor, { HexColor } from "@mapmycustomers/shared/types/AnyColor";
import anyColorToHex from "@mapmycustomers/shared/util/color/anyColorToHex";
import getContrastColor from "@mapmycustomers/shared/util/color/getContrastColor";
import { Avatar, EntityTypeShapedIcon, fileTypeToIcons, getFileType } from "@mapmycustomers/ui";

import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";
import GlobalSearchItemType from "@app/enum/GlobalSearchItemType";
import GlobalSearchItem, {
  GlobalSearchActivity,
  GlobalSearchResource,
} from "@app/types/globalSearch/GlobalSearchItem";

import { isSearchGroup } from "../../util/assert";

import styles from "./Item.module.scss";

interface Props {
  item: GlobalSearchItem;
}

const Icon = ({ item }: Props) => {
  const backgroundColor: HexColor | undefined = useMemo(() => {
    let result = undefined;
    if (item.entity) {
      let color;
      if (isSearchGroup(item.entity)) {
        color = item.entity.color ?? "grey";
      } else if (item.entityType === EntityType.TERRITORY) {
        color = item.entity.color ?? "grey";
      }
      if (color) {
        result = anyColorToHex(color as AnyColor);
      }
    }
    return result;
  }, [item]);

  if (item.entityType === EntityType.USER) {
    return <Avatar size="m" user={item.entity} />;
  }

  if (item.entityType === EntityType.ACTIVITY) {
    return (
      <ActivityTypeIcon activityType={(item.entity as GlobalSearchActivity).crmActivityType} />
    );
  }

  if (item.entityType === EntityType.RESOURCE) {
    const resource = item.entity as GlobalSearchResource;
    return (
      <div className={cn(styles.icon, styles.resource)}>
        <FontAwesomeIcon
          icon={resource.file ? fileTypeToIcons[getFileType(resource.file)].solid : faLink}
        />
      </div>
    );
  }

  if (item.entityType) {
    return (
      <EntityTypeShapedIcon
        className={cn(styles.icon, {
          [styles.blackIcon]: backgroundColor && getContrastColor(backgroundColor) === "black",
        })}
        entityType={item.entityType}
        style={backgroundColor ? { backgroundColor } : undefined}
      />
    );
  }

  return (
    <div className={cn(styles.icon, styles.queryIcon)}>
      <FontAwesomeIcon icon={item.type === GlobalSearchItemType.QUERY ? faClock : faRedoAlt} />
    </div>
  );
};

export default Icon;
